import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { useAppDispatch } from '@/store/hooks'
import { resetDepositFlow, setDepositCurrencyCode, setDepositStep } from '@/store/slices/paymentSlice'

interface DepositModalContextValue {
	isOpen: boolean
	openDeposit: (currencyCode?: string) => void
	close: () => void
}

const DepositModalContext = createContext<DepositModalContextValue | undefined>(undefined)

const CLOSE_ANIMATION_MS = 200

export function DepositModalProvider({ children }: { children: ReactNode }) {
	const dispatch = useAppDispatch()
	const [isOpen, setIsOpen] = useState(false)

	const openDeposit = useCallback(
		(currencyCode?: string) => {
			dispatch(resetDepositFlow())
			dispatch(setDepositStep('method'))
			dispatch(setDepositCurrencyCode(currencyCode ?? null))
			setIsOpen(true)
		},
		[dispatch],
	)

	const close = useCallback(() => {
		setIsOpen(false)
		window.setTimeout(() => {
			dispatch(resetDepositFlow())
		}, CLOSE_ANIMATION_MS)
	}, [dispatch])

	const value = useMemo(
		() => ({
			isOpen,
			openDeposit,
			close,
		}),
		[isOpen, openDeposit, close],
	)

	return <DepositModalContext.Provider value={value}>{children}</DepositModalContext.Provider>
}

export function useDepositModal() {
	const context = useContext(DepositModalContext)
	if (!context) {
		throw new Error('useDepositModal must be used within a DepositModalProvider')
	}
	return context
}
